import {Component, computed, Injectable, Input, signal, Signal} from '@angular/core';
import {MatCheckboxChange, MatCheckboxModule} from '@angular/material/checkbox';
import {OrganizationResponse, OrganizationUIConfig, ResourceSearchSchema} from '@middleware';
import {OrgService} from '@services/organization/org.service';
import {Filter, FilterVisibility} from '../filter';

@Component({
  template: `<ul class="bulletless">
    @for (org of _orgs(); track org.id) {
      <li
        ><mat-checkbox
          [value]="org.id"
          [checked]="filter.selectedValues().includes(org.id)"
          (change)="changeSelection($event)"
          >{{ org.full_name }}</mat-checkbox
        ></li
      >
    }
  </ul>`,
  imports: [MatCheckboxModule],
})
class OrganizationList<F extends OrganizationFilter> {
  get _orgs(): Signal<(OrganizationResponse & OrganizationUIConfig)[]> {
    return this.filter._orgs;
  }

  constructor() {}
  @Input() filter: F;

  changeSelection($event: MatCheckboxChange) {
    if ($event.checked) {
      this.filter.selectedValues.update(values => [...values, $event.source.value]);
    } else {
      this.filter.remove($event.source.value);
    }
  }
}

abstract class OrganizationFilter implements Filter<'Resources'> {
  abstract key;
  abstract label;
  abstract apply(filterBody: ResourceSearchSchema): void;
  abstract component;
  selectedValues = signal<string[]>([]);
  selected = computed(() => {
    const selected = this.selectedValues();
    const orgs = this._orgs();
    if (!orgs) {
      return selected.map(value => ({label: value, value}));
    }
    return selected.map(value => ({value, label: orgs.find(org => org.id == value)?.full_name ?? value}));
  });
  defaultExpanded = false;
  _orgs: Signal<(OrganizationResponse & OrganizationUIConfig)[]>;
  load(values: string[]): void {
    this.selectedValues.set(values);
  }
  remove(value: string) {
    this.selectedValues.update(values => values.filter(v => v != value));
  }

  constructor(_orgs: Signal<(OrganizationResponse & OrganizationUIConfig)[]>) {
    this._orgs = computed(() => {
      const orgs = _orgs();
      if (orgs.some(o => !o.config_data)) {
        console.log(
          'No config data for some orgs!',
          orgs.filter(o => !o.config_data).map(o => o.name),
        );
      } else {
        return [...orgs].sort(
          (a, b) => <number>a.config_data['display_order'] - <number>b.config_data['display_order'],
        );
      }
      return orgs;
    });
  }

  abstract visibility: FilterVisibility;
}

@Injectable()
export class AudienceFilter extends OrganizationFilter {
  key = 'audience' as const;
  label = 'Audience';
  component = OrganizationList<AudienceFilter>;
  apply(filterBody: ResourceSearchSchema): void {
    const selected = this.selectedValues();
    if (selected.length == 0) return;
    filterBody.target_audiences = {
      terms: selected.map(org => this._orgs().find(o => o.id == org)?.name).filter(n => !!n),
      match_all: false,
    };
  }
  add(value: string) {
    this.selectedValues.update(values => [...values, value]);
  }
  constructor(orgService: OrgService) {
    super(
      computed(() =>
        orgService
          .organizationArray()
          .map(org => (org.full_name === 'Other/External' ? {...org, full_name: 'Public'} : org)),
      ),
    );
  }
  visibility = FilterVisibility.EVERYONE;
}
